import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { pnaAvailabilityQueryOptions } from "~/domains/pna/pnaQueries";
import { toAvailabilityOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import { selectCanQueryAvailability, selectReferenceMaterial } from "~/features/configurator/Linear/redux/selectors";
import { useLinearProductInfo } from "~/features/configurator/Linear/redux/useLinearProductInfo";

export const useLinearAvailability = () => {
  const configuratorProduct = useLinearProductInfo();
  const canQuery = useSelector(selectCanQueryAvailability);

  // Reference material is the availability refresh signal and gate, not query identity.
  const referenceMaterial = useSelector(selectReferenceMaterial);
  const hasRefreshSignal = Boolean(referenceMaterial?.trim());
  const productCode = configuratorProduct.hybrisCode;

  const availabilityOptions = useMemo(
    () =>
      pnaAvailabilityQueryOptions({
        productCode,
        configId: configuratorProduct.configId,
        enabled: canQuery && hasRefreshSignal,
      }),
    [canQuery, configuratorProduct.configId, hasRefreshSignal, productCode],
  );
  const availabilityQuery = useQuery(availabilityOptions);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (canQuery && hasRefreshSignal) {
      void queryClient.invalidateQueries({ queryKey: availabilityOptions.queryKey, exact: true });
    }
  }, [availabilityOptions.queryKey, canQuery, hasRefreshSignal, queryClient, referenceMaterial]);
  const availability = toAvailabilityOutcomeFromQuery(availabilityQuery, availabilityOptions.enabled === true);

  const data = availability.status === "success" ? availability.data : undefined;
  if (data) {
    let leadTime = null;
    const leadTimeDate = data.packagingInformation[0]?.replenishmentLeadTimeDate;
    if (leadTimeDate) {
      leadTime = Math.ceil(dayjs(leadTimeDate).diff(dayjs(), "day") / 7);
    }
    return {
      leadTime: leadTime,
      errorLoadingAvailability: false,
      isLoadingAvailability: false,
    };
  }
  return {
    leadTime: null,
    errorLoadingAvailability: availability.status === "error",
    isLoadingAvailability: availability.status === "pending",
    isQueryAllowed: canQuery,
  };
};
